/**
 * chat-wait-sponsor-view.ts — Lit view for the ephemeral wait-slot ad.
 *
 * Deliberately a plain `render*` function rather than a custom element:
 * the slot must never participate in the transcript's virtualization, must
 * not own a `data-msg-id`, and must not be reachable by thread search.
 * Rendering it as a sibling node of the activity strip is what guarantees
 * all three.
 *
 * The creative rotates on a timer owned by the *caller* (the thread shell),
 * so a rotation never touches the message list. Height is fixed at
 * WAIT_SPONSOR_SLOT_HEIGHT_PX so token streaming does not reflow the pane.
 */
import { html, nothing, type TemplateResult } from "lit";
import {
  resolveWaitSponsorCreative,
  WAIT_SPONSOR_CREATIVES,
  WAIT_SPONSOR_SLOT_HEIGHT_PX,
  type WaitSponsorCreative,
} from "./chat-wait-sponsor.ts";

export type WaitSponsorSlotProps = {
  /** False once the run is terminal — the slot unmounts entirely. */
  visible: boolean;
  /** Rotation index; the shell advances it on its own timer. */
  rotationIndex: number;
  /** Current run id, surfaced to the shell for impression records. */
  runId: string | null;
  creatives?: readonly WaitSponsorCreative[];
  /**
   * Fires on pointerdown, *before* the Stop RPC resolves, so the slot can
   * unmount on the same tick the operator presses Stop. The Stop button
   * itself is outside this element — the hook exists so a press anywhere
   * in the slot collapses it immediately rather than waiting on the abort
   * round-trip.
   */
  onSlotPointerDown?: (event: PointerEvent) => void;
};

/** Decorative artwork. Inline SVG so the slot has no network dependency. */
function renderSponsorArtwork(kind: string | undefined): TemplateResult | typeof nothing {
  if (kind === "warm-pool") {
    return html`
      <svg class="chat-wait-sponsor__art" viewBox="0 0 160 96" aria-hidden="true" focusable="false">
        <rect x="0" y="0" width="160" height="96" fill="none" />
        <ellipse cx="80" cy="30" rx="58" ry="14" fill="currentColor" opacity="0.10" />
        <ellipse cx="80" cy="30" rx="42" ry="9" fill="currentColor" opacity="0.10" />
        <rect x="26" y="46" width="22" height="40" rx="3" fill="currentColor" opacity="0.16" />
        <rect x="54" y="38" width="22" height="48" rx="3" fill="currentColor" opacity="0.20" />
        <rect x="82" y="50" width="22" height="36" rx="3" fill="currentColor" opacity="0.14" />
        <rect x="110" y="42" width="22" height="44" rx="3" fill="currentColor" opacity="0.18" />
        <rect x="30" y="54" width="4" height="4" fill="currentColor" opacity="0.55" />
        <rect x="30" y="66" width="4" height="4" fill="currentColor" opacity="0.40" />
        <rect x="58" y="46" width="4" height="4" fill="currentColor" opacity="0.55" />
        <rect x="58" y="58" width="4" height="4" fill="currentColor" opacity="0.35" />
        <rect x="58" y="70" width="4" height="4" fill="currentColor" opacity="0.50" />
        <rect x="86" y="58" width="4" height="4" fill="currentColor" opacity="0.45" />
        <rect x="114" y="50" width="4" height="4" fill="currentColor" opacity="0.50" />
        <rect x="114" y="64" width="4" height="4" fill="currentColor" opacity="0.32" />
      </svg>
    `;
  }
  if (kind === "context-window") {
    return html`
      <svg class="chat-wait-sponsor__art" viewBox="0 0 160 96" aria-hidden="true" focusable="false">
        <rect x="22" y="20" width="116" height="14" rx="4" fill="currentColor" opacity="0.16" />
        <rect x="22" y="42" width="92" height="14" rx="4" fill="currentColor" opacity="0.22" />
        <rect x="22" y="64" width="64" height="14" rx="4" fill="currentColor" opacity="0.14" />
        <circle
          cx="126"
          cy="71"
          r="12"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          opacity="0.5"
        />
        <line
          x1="135"
          y1="80"
          x2="142"
          y2="87"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          opacity="0.5"
        />
      </svg>
    `;
  }
  if (kind === "observability") {
    return html`
      <svg class="chat-wait-sponsor__art" viewBox="0 0 160 96" aria-hidden="true" focusable="false">
        <polyline
          points="20,74 46,52 68,62 96,30 140,40"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.45"
        />
        <circle cx="20" cy="74" r="4" fill="currentColor" opacity="0.5" />
        <circle cx="46" cy="52" r="4" fill="currentColor" opacity="0.5" />
        <circle cx="68" cy="62" r="4" fill="currentColor" opacity="0.5" />
        <circle cx="96" cy="30" r="4" fill="currentColor" opacity="0.5" />
        <circle cx="140" cy="40" r="4" fill="currentColor" opacity="0.5" />
      </svg>
    `;
  }
  return nothing;
}

/**
 * Render the slot. Returns `nothing` when the run is terminal so the shell
 * can splice it in unconditionally.
 */
export function renderWaitSponsorSlot(
  props: WaitSponsorSlotProps,
): TemplateResult | typeof nothing {
  if (!props.visible) {
    return nothing;
  }
  const creative = resolveWaitSponsorCreative(
    props.rotationIndex,
    props.creatives ?? WAIT_SPONSOR_CREATIVES,
  );
  if (!creative) {
    return nothing;
  }
  return html`
    <aside
      class="chat-wait-sponsor"
      data-testid="chat-wait-sponsor"
      data-slot="wait-sponsor"
      data-creative-id=${creative.id}
      data-run-id=${props.runId ?? ""}
      style="--chat-wait-sponsor-h:${WAIT_SPONSOR_SLOT_HEIGHT_PX}px"
      aria-label="Sponsored message"
      data-testid-does-not-cover-send="true"
      @pointerdown=${props.onSlotPointerDown}
    >
      <div class="chat-wait-sponsor__rule" aria-hidden="true"></div>
      <span class="chat-wait-sponsor__disclosure">${creative.disclosure}</span>
      <div class="chat-wait-sponsor__body">
        <div class="chat-wait-sponsor__copy">
          <p class="chat-wait-sponsor__headline">${creative.headline}</p>
          <p class="chat-wait-sponsor__text">${creative.body}</p>
        </div>
        ${renderSponsorArtwork(creative.artwork)}
      </div>
      <span class="chat-wait-sponsor__badge" aria-hidden="true">Ad</span>
    </aside>
  `;
}
